import { Command, Plugin } from '@ckeditor/ckeditor5-core';
import { Widget, toWidget, viewToModelPositionOutsideModelElement } from '@ckeditor/ckeditor5-widget';
import { ButtonView } from '@ckeditor/ckeditor5-ui';
import { SchemaContextDefinition } from '@ckeditor/ckeditor5-engine/src/model/schema';
import { FileInput } from 'ngx-material-file-input';

const FILE_URL_BLOCK_CLASS_NAME = 'ck-file-url';

class FileUploadPickFileCommand extends Command {
  execute() {
    const fileUploaderConfig = this.editor.config.get('fileUpload') as any;
    const fileUploaderFn = fileUploaderConfig.uploaderFn;

    if (!fileUploaderFn) {
      return;
    }

    const fileInputField = document.createElement('input');

    fileInputField.type = 'file';
    fileInputField.name = 'file-upload';
    fileInputField.style.visibility = 'hidden';
    fileInputField.style.width = '0px';
    fileInputField.style.height = '0px';

    this.editor.ui.element.appendChild(fileInputField);

    fileInputField.onchange = async () => {
      if (!fileInputField.files?.length) {
        return;
      }

      try {
        const fileUrl = await fileUploaderFn(fileInputField.files[0]);

        this.editor.execute('file-upload:insert-url', { url: fileUrl });
      } catch (error) {
        console.error('Error uploading file to content server');
      }
    };

    fileInputField.click();
  }
}

class FileUploadInsertCommand extends Command {
  execute({ url }) {
    const editor = this.editor;
    const selection = editor.model.document.selection;

    editor.model.change((writer) => {
      const urlBlock = writer.createElement('uploadedUrl', {
        ...Object.fromEntries(selection.getAttributes()),
        url,
      });

      editor.model.insertContent(urlBlock);
    });
  }
}

class FileUploadPlugin extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    const editor = this.editor;
    const t = editor.t;

    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('file-upload:insert-url', new FileUploadInsertCommand(this.editor));
    this.editor.commands.add('file-upload:pick-file', new FileUploadPickFileCommand(this.editor));

    const fileUploaderConfig = this.editor.config.get('fileUpload') as any;
    const uploaderFn = fileUploaderConfig?.uploaderFn;

    editor.ui.componentFactory.add('fileupload', () => {
      const button = new ButtonView();

      button.set({
        icon: `<?xml version="1.0" encoding="utf-8"?><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve">
        <g>
          <path d="M131.5,348.8c-24,0-45.3-9.3-60.8-24c-15.4-14.8-24.6-34.7-24.7-56.7c0-22.1,9.2-41.9,24.7-56.7c15.4-14.7,36.8-24,60.8-24   c10.8,0,20.1-7.5,22.5-18c4.9-21.7,17.9-40.3,36.5-53.8C209,102.1,232.9,94,258.2,94c7.1,0,14.4,0.6,21.6,2l0,0   c25.8,4.7,47.1,17.1,61.9,33.6c14.8,16.5,23,36.7,23,57.8c0,6-0.7,12-2,18.1c-1.5,6.8,0.1,14,4.5,19.5c4.4,5.5,11,8.6,18,8.6h23.1   c31.8,0.1,57.6,25.8,57.6,57.6c-0.1,31.8-25.8,57.6-57.6,57.6h-23.1c-12.7,0-23.1,10.3-23.1,23.1c0,12.7,10.3,23.1,23.1,23.1h23.1   c57.3,0,103.7-46.4,103.8-103.8c0-57.3-46.4-103.7-103.8-103.8h-23.1v23.1l22.5,5c2.1-9.4,3.1-18.9,3.1-28.2   c0-33.2-13-64.4-34.8-88.6c-21.8-24.2-52.4-41.6-87.9-48.1l0,0c-10-1.8-20-2.7-29.9-2.7c-35,0-68.2,11-94.9,30.4   c-26.6,19.3-46.8,47.4-54.3,81l22.5,5.1v-23.1c-36,0-68.8,14-92.6,36.8C15,200.8,0,232.9,0,268.1c0,35.3,15,67.3,38.9,90   c23.8,22.8,56.7,36.8,92.6,36.8c12.7,0,23.1-10.3,23.1-23.1C154.6,359.1,144.3,348.8,131.5,348.8L131.5,348.8z"/>
          <path d="M194,319l52.9-52.9l52.9,52.9c9,9,23.6,9,32.6,0s9-23.6,0-32.6l-69.2-69.2c-9-9-23.6-9-32.6,0l-69.2,69.2   c-9,9-9,23.6,0,32.6S185,328,194,319L194,319z"/>
          <path d="M223.8,233.5v207.5c0,12.7,10.3,23.1,23.1,23.1s23.1-10.3,23.1-23.1V233.5c0-12.7-10.3-23.1-23.1-23.1   S223.8,220.8,223.8,233.5"/>
        </g>
        </svg>`,
        withText: false,
      });

      this.editor.on('file-upload:disable', () => (button.isEnabled = false));
      this.editor.on('file-upload:enable', () => (button.isEnabled = true));

      if (!uploaderFn) {
        button.isEnabled = false;
      }

      this.listenTo(button, 'execute', () => {
        this.editor.execute('file-upload:pick-file');
      });

      return button;
    });
  }

  private defineSchema() {
    const schema = this.editor.model.schema;

    schema.register('uploadedUrl', {
      // Allow wherever text is allowed:
      allowWhere: '$text',

      // The variables will act as an inline node:
      isInline: true,

      // The inline widget is self-contained so it cannot be split by the caret and it can be selected:
      isObject: true,

      // The inline widget can have the same attributes as text (for example linkHref, bold).
      allowAttributesOf: '$text',

      // The variables can have many types, like date, name, surname, etc:
      allowAttributes: ['url'],
    });
  }

  private defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: [FILE_URL_BLOCK_CLASS_NAME],
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Extract the "name" from "{name}".
        const name = (viewElement.getChild(0) as any).data.slice(1, -1);

        return modelWriter.createElement('uploadedUrl', { name });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'uploadedUrl',
      view: (modelItem, { writer: viewWriter }) => {
        const widgetElement = createFileUrlView(modelItem, viewWriter);

        // Enable widget handling on a variables element inside the editing view.
        return toWidget(widgetElement, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'uploadedUrl',
      view: (modelItem, { writer: viewWriter }) => createFileUrlView(modelItem, viewWriter),
    });

    // Helper method for both downcast converters.
    function createFileUrlView(modelItem, viewWriter) {
      const url = modelItem.getAttribute('url');

      const variablesView = viewWriter.createContainerElement('span', {
        class: FILE_URL_BLOCK_CLASS_NAME,
      });

      // Insert the variables name (as a text).
      const innerText = viewWriter.createText(url);
      viewWriter.insert(viewWriter.createPositionAt(variablesView, 0), innerText);

      return variablesView;
    }
  }
}

export default FileUploadPlugin;
